import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMatterById } from '@/matter/store/selectors'
import { selectAgentConversationFormHasJurisdictionFilters } from '@/agent-v2/chat-form/store/selectors'
import { researchFiltersActions } from '../sources-dialog/store/slice'

export type UseJurisdictionPrefillArgs = {
  conversationId: string
  /** The conversation's matter, with a URL fallback until the record exists. */
  matterId: string | null
  /** True once any persisted filters for this conversation have been restored — or the consumer has none to restore. */
  prefillReady: boolean
}

/**
 * Prefill research filters from the matter's default jurisdiction.
 *
 * Dispatches only prefillFromJurisdiction — the researchFilters listener owns syncing the
 * resulting sources into the form request params, so consumers must not dispatch
 * syncResearchFiltersToRequestParams themselves.
 *
 * Runs whenever the guards allow; the returned callback lets consumers also trigger it from
 * user actions (e.g. opening the sources dialog) under the same guard set.
 */
export function useJurisdictionPrefill({ conversationId, matterId, prefillReady }: UseJurisdictionPrefillArgs) {
  const dispatch = useAppDispatch()
  const hasJurisdictionFilters = useAppSelector((state: RootState) => selectAgentConversationFormHasJurisdictionFilters(state, { conversationId }))
  const defaultJurisdiction = useAppSelector((state: RootState) => selectMatterById(state, { matterId })?.default_jurisdiction ?? null)

  const prefillDefaultResearchSources = useCallback(() => {
    if (!prefillReady) return
    if (hasJurisdictionFilters) return
    if (!defaultJurisdiction) return

    dispatch(researchFiltersActions.prefillFromJurisdiction({ conversationId, jurisdiction: defaultJurisdiction }))
  }, [conversationId, defaultJurisdiction, dispatch, hasJurisdictionFilters, prefillReady])

  useEffect(() => {
    prefillDefaultResearchSources()
  }, [prefillDefaultResearchSources])

  return prefillDefaultResearchSources
}
